import React, { useState, useEffect } from 'react';
import { CheckSquare, Plus, Calendar, User, Trash2, Edit2, Check, X, Flag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast, ToastContainer } from '../componets/Toast';
import API_URL from '../config/API_URL';
import moment from 'moment';

const Tasks = () => {
  const [todos, setTodos] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'completed'
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const { toasts, removeToast, showSuccess, showError } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'pending',
    due_date: '',
    contact_id: '',
  });

  const styles = {
    page: {
      padding: '2rem',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    header: {
      marginBottom: '2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    title: {
      fontSize: '2rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 800,
      color: '#0f172a',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    addButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.5rem',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
    },
    filterBar: {
      display: 'flex',
      gap: '0.75rem',
      marginBottom: '2rem',
      flexWrap: 'wrap',
    },
    filterButton: {
      padding: '0.5rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      background: 'white',
      color: '#64748b',
      fontSize: '0.9rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 200ms',
    },
    filterButtonActive: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      borderColor: 'transparent',
    },
    todoList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    todoCard: {
      background: 'white',
      borderRadius: '12px',
      padding: '1.5rem',
      border: '2px solid #e2e8f0',
      display: 'flex',
      gap: '1rem',
      alignItems: 'flex-start',
      transition: 'all 200ms',
    },
    checkbox: {
      width: '24px',
      height: '24px',
      borderRadius: '8px',
      border: '2px solid #cbd5e1',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      transition: 'all 200ms',
    },
    checkboxChecked: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderColor: 'transparent',
    },
    todoContent: {
      flex: 1,
      minWidth: 0,
    },
    todoTitle: {
      fontSize: '1.1rem',
      fontWeight: 600,
      color: '#0f172a',
      marginBottom: '0.5rem',
      wordBreak: 'break-word',
    },
    todoTitleCompleted: {
      textDecoration: 'line-through',
      color: '#94a3b8',
    },
    todoDescription: {
      fontSize: '0.9rem',
      color: '#64748b',
      marginBottom: '0.75rem',
      lineHeight: 1.5,
    },
    todoMeta: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.75rem',
      alignItems: 'center',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.25rem 0.75rem',
      borderRadius: '6px',
      fontSize: '0.8rem',
      fontWeight: 500,
    },
    todoActions: {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center',
    },
    iconButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '0.5rem',
      borderRadius: '8px',
      color: '#64748b',
      transition: 'all 200ms',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem',
    },
    modalContent: {
      background: 'white',
      borderRadius: '16px',
      padding: '2rem',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      paddingBottom: '1rem',
      borderBottom: '1px solid #e2e8f0',
    },
    modalTitle: {
      fontSize: '1.5rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      color: '#0f172a',
    },
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      color: '#0f172a',
      fontWeight: 500,
      fontSize: '0.95rem',
    },
    input: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      transition: 'all 200ms',
    },
    textarea: {
      width: '95%',
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
    },
    select: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      cursor: 'pointer',
    },
    modalActions: {
      display: 'flex',
      gap: '1rem',
      marginTop: '2rem',
    },
    button: {
      flex: 1,
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
    },
    emptyState: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#64748b',
    },
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [todosRes, contactsRes] = await Promise.all([
        fetch(`${API_URL}/api/todos`, { credentials: 'include' }),
        fetch(`${API_URL}/api/contacts`, { credentials: 'include' }),
      ]);

      const todosData = await todosRes.json();
      const contactsData = await contactsRes.json();

      setTodos(todosData.todos || []);
      setContacts(contactsData.contacts || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      showError('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (todo = null) => {
    if (todo) {
      setEditingTodo(todo);
      setFormData({
        title: todo.title,
        description: todo.description || '',
        priority: todo.priority,
        status: todo.status,
        due_date: todo.due_date ? moment(todo.due_date).format('YYYY-MM-DDTHH:mm') : '',
        contact_id: todo.contact?.id || '',
      });
    } else {
      setEditingTodo(null);
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        status: 'pending',
        due_date: '',
        contact_id: '',
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingTodo(null);
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      status: 'pending',
      due_date: '',
      contact_id: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      showError('Title is required');
      return;
    }

    try {
      const payload = {
        ...formData,
        contact_id: formData.contact_id || null,
        due_date: formData.due_date || null,
      };

      const url = editingTodo
        ? `${API_URL}/api/todos/${editingTodo.id}`
        : `${API_URL}/api/todos`;

      const response = await fetch(url, {
        method: editingTodo ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        showSuccess(editingTodo ? 'Task updated!' : 'Task created!');
        handleCloseModal();
        loadData();
      } else {
        showError('Failed to save task');
      }
    } catch (error) {
      console.error('Failed to save task:', error);
      showError('Failed to save task');
    }
  };

  const handleToggle = async (todoId) => {
    try {
      const response = await fetch(`${API_URL}/api/todos/${todoId}/toggle`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        loadData();
      }
    } catch (error) {
      console.error('Failed to toggle task:', error);
    }
  };

  const handleDelete = async (todoId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      const response = await fetch(`${API_URL}/api/todos/${todoId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        showSuccess('Task deleted');
        loadData();
      } else {
        showError('Failed to delete task');
      }
    } catch (error) {
      console.error('Failed to delete task:', error);
      showError('Failed to delete task');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return { bg: '#fee2e2', text: '#dc2626' };
      case 'medium':
        return { bg: '#fef3c7', text: '#d97706' };
      case 'low':
        return { bg: '#dbeafe', text: '#2563eb' };
      default:
        return { bg: '#f1f5f9', text: '#64748b' };
    }
  };

  const filteredTodos = todos.filter((todo) => {
    if (filter === 'all') return true;
    if (filter === 'pending') return todo.status !== 'completed';
    if (filter === 'completed') return todo.status === 'completed';
    return true;
  });

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={{ textAlign: 'center', padding: '4rem' }}>Loading tasks...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div style={styles.header}>
        <h1 style={styles.title}>
          <CheckSquare size={32} />
          Tasks
        </h1>
        <button
          style={styles.addButton}
          onClick={() => handleOpenModal()}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 16px rgba(102, 126, 234, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <Plus size={20} />
          New Task
        </button>
      </div>

      <div style={styles.filterBar}>
        {['all', 'pending', 'completed'].map((filterOption) => (
          <button
            key={filterOption}
            style={{
              ...styles.filterButton,
              ...(filter === filterOption ? styles.filterButtonActive : {}),
            }}
            onClick={() => setFilter(filterOption)}
          >
            {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
            {filterOption === 'all' && ` (${todos.length})`}
            {filterOption === 'pending' &&
              ` (${todos.filter((t) => t.status !== 'completed').length})`}
            {filterOption === 'completed' &&
              ` (${todos.filter((t) => t.status === 'completed').length})`}
          </button>
        ))}
      </div>

      {filteredTodos.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.emptyState}
        >
          <CheckSquare size={64} style={{ opacity: 0.3, marginBottom: '1rem' }} />
          <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>No tasks yet</h3>
          <p>Create your first task to get started!</p>
        </motion.div>
      ) : (
        <div style={styles.todoList}>
          <AnimatePresence>
            {filteredTodos.map((todo, index) => {
              const priorityColor = getPriorityColor(todo.priority);
              const isCompleted = todo.status === 'completed';
              const isOverdue =
                todo.due_date && !isCompleted && moment(todo.due_date).isBefore(moment());

              return (
                <motion.div
                  key={todo.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: index * 0.05 }}
                  style={styles.todoCard}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#cbd5e1';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div
                    style={{
                      ...styles.checkbox,
                      ...(isCompleted ? styles.checkboxChecked : {}),
                    }}
                    onClick={() => handleToggle(todo.id)}
                  >
                    {isCompleted && <Check size={16} color="white" />}
                  </div>

                  <div style={styles.todoContent}>
                    <div
                      style={{
                        ...styles.todoTitle,
                        ...(isCompleted ? styles.todoTitleCompleted : {}),
                      }}
                    >
                      {todo.title}
                    </div>

                    {todo.description && (
                      <div style={styles.todoDescription}>{todo.description}</div>
                    )}

                    <div style={styles.todoMeta}>
                      <span
                        style={{
                          ...styles.badge,
                          background: priorityColor.bg,
                          color: priorityColor.text,
                        }}
                      >
                        <Flag size={14} />
                        {todo.priority}
                      </span>

                      {todo.due_date && (
                        <span
                          style={{
                            ...styles.badge,
                            background: isOverdue ? '#fee2e2' : '#f0f9ff',
                            color: isOverdue ? '#dc2626' : '#0284c7',
                          }}
                        >
                          <Calendar size={14} />
                          {moment(todo.due_date).format('MMM D, YYYY')}
                          {isOverdue && ' (Overdue)'}
                        </span>
                      )}

                      {todo.contact && (
                        <span
                          style={{
                            ...styles.badge,
                            background: '#f0fdf4',
                            color: '#16a34a',
                          }}
                        >
                          <User size={14} />
                          {todo.contact.name}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={styles.todoActions}>
                    <button
                      style={styles.iconButton}
                      onClick={() => handleOpenModal(todo)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f1f5f9';
                        e.currentTarget.style.color = '#667eea';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'none';
                        e.currentTarget.style.color = '#64748b';
                      }}
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      style={styles.iconButton}
                      onClick={() => handleDelete(todo.id)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#fee2e2';
                        e.currentTarget.style.color = '#dc2626';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'none';
                        e.currentTarget.style.color = '#64748b';
                      }}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.modal}
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={styles.modalContent}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>
                  {editingTodo ? 'Edit Task' : 'Create New Task'}
                </h3>
                <button
                  style={styles.iconButton}
                  onClick={handleCloseModal}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Reach out to John"
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Add details about this task..."
                    style={styles.textarea}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Priority</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      style={styles.select}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      style={styles.select}
                    >
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Due Date</label>
                  <input
                    type="datetime-local"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Assign Contact (Optional)</label>
                  <select
                    value={formData.contact_id}
                    onChange={(e) => setFormData({ ...formData, contact_id: e.target.value })}
                    style={styles.select}
                  >
                    <option value="">No contact</option>
                    {contacts.map((contact) => (
                      <option key={contact.id} value={contact.id}>
                        {contact.name} {contact.company ? `- ${contact.company}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.modalActions}>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    style={{
                      ...styles.button,
                      background: '#e2e8f0',
                      color: '#64748b',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      ...styles.button,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                    }}
                  >
                    {editingTodo ? 'Update Task' : 'Create Task'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Tasks;